import {Alert, Anchor, Button, Group, Modal, Stack, Text} from '@mantine/core';
import React, {useCallback, useRef, useState} from 'react';

export const PRIVACY_POLICY_URL = 'https://www.benchling.com/privacy-policy';
export const VOICE_POLICY_URL = 'https://www.benchling.com/voice-policy';

export interface RealtimeVoiceConsentModalProps {
  opened: boolean;
  onCancel: () => void;
  onConsentGranted: () => void;
  onGrantConsent: () => Promise<void>;
}

export default function RealtimeVoiceConsentModal({
  opened,
  onCancel,
  onConsentGranted,
  onGrantConsent,
}: RealtimeVoiceConsentModalProps): JSX.Element {
  const grantInFlightRef = useRef(false);
  const [isGranting, setIsGranting] = useState(false);
  const [hasPersistenceError, setHasPersistenceError] = useState(false);

  const handleCancel = useCallback((): void => {
    if (grantInFlightRef.current) {
      return;
    }
    setHasPersistenceError(false);
    onCancel();
  }, [onCancel]);

  const handleGrantConsent = useCallback(async (): Promise<void> => {
    if (grantInFlightRef.current) {
      return;
    }

    grantInFlightRef.current = true;
    setIsGranting(true);
    setHasPersistenceError(false);
    const consentWasStored = await onGrantConsent().then(
      () => true,
      () => false
    );
    setIsGranting(false);
    grantInFlightRef.current = false;
    if (consentWasStored) {
      onConsentGranted();
    } else {
      setHasPersistenceError(true);
    }
  }, [onConsentGranted, onGrantConsent]);

  return (
    <Modal
      centered={true}
      closeOnClickOutside={!isGranting}
      closeOnEscape={!isGranting}
      onClose={handleCancel}
      opened={opened}
      title="Before you start Voice"
      withCloseButton={false}
    >
      <Stack spacing="md">
        <Text>
          By using Voice, you acknowledge and agree to Benchling's{' '}
          <Anchor href={VOICE_POLICY_URL} rel="noopener noreferrer" target="_blank">
            Voice Policy
          </Anchor>
          , including your responsibility to obtain consent from others present. You also acknowledge
          Benchling's{' '}
          <Anchor href={PRIVACY_POLICY_URL} rel="noopener noreferrer" target="_blank">
            Privacy Policy
          </Anchor>
          .
        </Text>
        {hasPersistenceError && (
          <Alert color="red" role="alert">
            We couldn't save your choice. Try again.
          </Alert>
        )}
        <Group position="right">
          <Button disabled={isGranting} onClick={handleCancel} variant="secondary">
            Not now
          </Button>
          <Button disabled={isGranting} loading={isGranting} onClick={handleGrantConsent}>
            Agree and continue
          </Button>
        </Group>
      </Stack>
    </Modal>
  );
}
